import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  IoAdd,
  IoLocationOutline,
  IoCallOutline,
  IoMusicalNotes,
  IoTrashOutline,
} from "react-icons/io5";
import GearButton from "../../ui/gearbutton";
import GearInput from "../../ui/gearinput";
import { addLivePal, CreateLivePalData } from "../../backend/api/livepal";
import { getCoordinates } from "../../backend/api/geo";
import SystemSelect from "../../components/SystemSelect";
import { CustomCheckbox } from "../../components/CustomCheckbox";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  CreateLivePalFormData,
  CreateLivePalFormSchema,
} from "../../forms/CreateLivePalFormSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import Select from "react-select";

export default function AddLivePalForm() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isGeneratingCoords, setIsGeneratingCoords] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CreateLivePalFormData>({
    resolver: zodResolver(CreateLivePalFormSchema),
    defaultValues: {
      name: "",
      address: "",
      zipCode: "",
      city: "",
      region: "",
      country: "Italy",
      website: "",
      email: "",
      capacity: 1,
      venueType: "acoustic",
      accommodation: false,
      meals: false,
      coordinates: { lat: 0, lng: 0 },
      contacts: [],
      equipment: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contacts",
  });

  const { mutate: addLivePalMutation, isPending: isAdding } = useMutation({
    mutationFn: (data: CreateLivePalData) => addLivePal(data, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-livepals"] });
      toast.success(t("forms.livePalCreated", "Locale aggiunto con successo!"));
      reset();
    },
    onError: (error: any) => {
      console.error("Errore aggiunta locale:", error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        t("errors.unknownError");
      toast.error(`${t("errors.error")}: ${msg}`);
    },
  });

  const onSubmit = async (data: CreateLivePalFormData) => {
    try {
      setIsGeneratingCoords(true);
      const coordsResponse = await getCoordinates(
        {
          address: data.address,
          city: data.city,
          zipCode: data.zipCode,
          country: data.country,
          palType: "LivePal",
        },
        i18n.language
      );

      const apiData = {
        ...data,
        coordinates: coordsResponse.data,
      } as CreateLivePalData;

      addLivePalMutation(apiData);
    } catch (err: any) {
      console.error(err);
      toast.error(t("errors.unknownError"));
    } finally {
      setIsGeneratingCoords(false);
    }
  };

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "rgb(51, 24, 85)",
      borderColor: state.isFocused ? "rgba(90, 24, 196, 0.6)" : "#6e11b04d",
      borderRadius: "0.5rem",
      fontSize: "14px",
      minHeight: "40px",
      color: "white",
      boxShadow: "none",
      "&:hover": {
        borderColor: "rgba(90, 24, 196, 0.6)",
      },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1F1235",
      borderRadius: "0.75rem",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      zIndex: 100,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#5A18C4"
        : state.isFocused
        ? "rgba(90, 24, 196, 0.2)"
        : "transparent",
      color: "white",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#5A18C4",
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#6a7282 !important",
      fontSize: "14px !important",
    }),
    input: (base: any) => ({
      ...base,
      color: "white",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.4)",
      "&:hover": {
        color: "white",
      },
    }),
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 w-full">
      {/* Informazioni Generali */}
      <div className="space-y-4">
        <GearInput
          {...register("name")}
          label={t("forms.venueName", "Nome del Locale")}
          placeholder={t("forms.venueName", "Nome del Locale")}
          error={errors.name?.message}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <GearInput
            type="number"
            {...register("capacity", { valueAsNumber: true })}
            label={t("forms.capacity", "Capacità")}
            placeholder="100"
            error={errors.capacity?.message}
          />
          <div className="flex flex-col mb-4">
            <label className="block text-white text-[14px] font-[400] mb-1">
              {t("forms.venueType", "Tipo di Locale")}
            </label>
            <Controller
              control={control}
              name="venueType"
              render={({ field }) => (
                <div className="flex gap-4 h-[42px] items-center">
                  <label className="flex items-center gap-2 text-white cursor-pointer group">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        field.value === "acoustic"
                          ? "border-secondary bg-secondary"
                          : "border-white/20"
                      }`}
                    >
                      {field.value === "acoustic" && (
                        <div className="w-2 rounded-full bg-white h-2" />
                      )}
                    </div>
                    <input
                      type="radio"
                      className="hidden"
                      onChange={() => field.onChange("acoustic")}
                      checked={field.value === "acoustic"}
                    />
                    <span className="text-sm">
                      {t("filters.acoustic", "Acoustic")}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-white cursor-pointer group">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        field.value === "fullBand"
                          ? "border-secondary bg-secondary"
                          : "border-white/20"
                      }`}
                    >
                      {field.value === "fullBand" && (
                        <div className="w-2 rounded-full bg-white h-2" />
                      )}
                    </div>
                    <input
                      type="radio"
                      className="hidden"
                      onChange={() => field.onChange("fullBand")}
                      checked={field.value === "fullBand"}
                    />
                    <span className="text-sm">
                      {t("filters.fullBand", "Full Band")}
                    </span>
                  </label>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      {/* Località */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#331855]/30 space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoLocationOutline size={20} className="text-primary" />{" "}
          {t("forms.location", "Località")}
        </h4>
        <GearInput
          {...register("address")}
          label={t("forms.address", "Indirizzo")}
          placeholder="Via/Piazza..."
          error={errors.address?.message}
        />
        <div className="grid grid-cols-2 gap-4">
          <GearInput
            {...register("zipCode")}
            label={t("forms.zipCode", "CAP")}
            placeholder="12345"
            error={errors.zipCode?.message}
          />
          <GearInput
            {...register("city")}
            label={t("forms.city", "Città")}
            placeholder="Roma"
            error={errors.city?.message}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <GearInput
            {...register("region")}
            label={t("forms.region", "Regione")}
            placeholder="Lazio"
            error={errors.region?.message}
          />
          <GearInput
            {...register("country")}
            label={t("forms.country", "Paese")}
            placeholder="Italy"
            error={errors.country?.message}
          />
        </div>
      </div>

      {/* Contatti */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#331855]/30 space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoCallOutline size={20} className="text-primary" />{" "}
          {t("forms.contacts", "Contatti")}
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <GearInput
            {...register("email")}
            label={t("forms.email", "Email")}
            placeholder="info@venue.com"
            error={errors.email?.message}
          />
          <GearInput
            {...register("website")}
            label={t("forms.website", "Sito Web")}
            placeholder="https://example.com"
            error={errors.website?.message}
          />
        </div>

        <div className="space-y-4 pt-2">
          <p className="text-xs text-secondary/70">
            {t(
              "forms.extraContactsHint",
              "Aggiungi altri social o riferimenti"
            )}
          </p>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="flex gap-2 items-start bg-[#1F1235]/50 p-3 rounded-lg border border-white/5"
            >
              <div className="flex-grow space-y-3">
                <Controller
                  control={control}
                  name={`contacts.${index}.type` as const}
                  render={({ field: subField }) => (
                    <div className="isMultiSelect">
                      <Select
                        styles={selectStyles}
                        options={[
                          { value: "email", label: "EMAIL" },
                          { value: "instagram", label: "INSTAGRAM" },
                          { value: "x", label: "X" },
                          { value: "linkedin", label: "LINKEDIN" },
                          { value: "facebook", label: "FACEBOOK" },
                          { value: "website", label: "WEBSITE" },
                          { value: "telegram", label: "TELEGRAM" },
                        ]}
                        value={[
                          { value: "email", label: "EMAIL" },
                          { value: "instagram", label: "INSTAGRAM" },
                          { value: "x", label: "X" },
                          { value: "linkedin", label: "LINKEDIN" },
                          { value: "facebook", label: "FACEBOOK" },
                          { value: "website", label: "WEBSITE" },
                          { value: "telegram", label: "TELEGRAM" },
                        ].find((o) => o.value === subField.value)}
                        onChange={(val: any) => subField.onChange(val.value)}
                        isSearchable={false}
                      />
                    </div>
                  )}
                />
                <GearInput
                  {...register(`contacts.${index}.value` as const)}
                  placeholder={t("forms.value", "Valore")}
                  containerClassName="mb-0"
                  error={errors.contacts?.[index]?.value?.message}
                />
              </div>
              <button
                type="button"
                onClick={() => remove(index)}
                className="mt-2.5 p-2 bg-red-500/10 text-red-500 rounded-full hover:bg-red-500/20 transition-colors"
              >
                <IoTrashOutline size={18} />
              </button>
            </div>
          ))}
          <GearButton
            type="button"
            variant="ghost"
            onClick={() => append({ type: "email", value: "" })}
            className="w-full !border-dashed !border-white/20 !bg-transparent hover:!bg-white/5"
          >
            <div className="flex items-center gap-2 justify-center text-white py-1">
              <IoAdd /> {t("forms.addContact", "Aggiungi Contatto")}
            </div>
          </GearButton>
        </div>
      </div>

      {/* Servizi & Equipment */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#331855]/30 space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoMusicalNotes size={20} className="text-primary" />{" "}
          {t("forms.servicesAndEquipment", "Servizi e Attrezzatura")}
        </h4>
        <div className="flex gap-8 py-2">
          <Controller
            control={control}
            name="accommodation"
            render={({ field }) => (
              <CustomCheckbox
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                label={t("filters.accommodation", "Alloggio")}
              />
            )}
          />
          <Controller
            control={control}
            name="meals"
            render={({ field }) => (
              <CustomCheckbox
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                label={t("filters.meals", "Pasti")}
              />
            )}
          />
        </div>

        <div className="mt-4 pt-2 border-t border-white/5">
          <label className="block text-white text-[14px] font-[400] mb-3">
            {t("forms.availableEquipment", "Attrezzatura Disponibile")}
          </label>
          <Controller
            control={control}
            name="equipment"
            render={({ field }) => (
              <SystemSelect
                value={field.value || []}
                onChange={field.onChange}
                useTranslationKeys={true}
              />
            )}
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <GearButton
          type="submit"
          variant="secondary"
          disabled={isAdding || isGeneratingCoords}
          className="w-full !rounded-full shadow-[0_0_20px_rgba(90,24,196,0.3)] hover:shadow-[0_0_30px_rgba(90,24,196,0.5)] transition-all"
        >
          <div className="flex items-center gap-2 justify-center">
            <IoAdd size={22} />
            <span className="text-[14px] mt-[-2px]">
              {isAdding || isGeneratingCoords
                ? t("common.saving", "Salvataggio...")
                : t("forms.addVenueButton", "Crea Locale")}
            </span>
          </div>
        </GearButton>
      </div>
    </form>
  );
}
